/* eslint-disable unicorn/prefer-array-some */
/**
 * V2 dimensional table helpers — progressive metadata + block outputs loading.
 * Re-exports shared helpers from TableHelpersOpz; only row building differs.
 */
import { blockOutputDisplayText } from 'utils/scaleBlockOutputPercentageValues';

import { dimensionalFooterRow, dimensionalHeaderRow } from './TableHelpersOpz';

export {
  buildTree,
  buildCellReference,
  getDimensionalColumns,
  getColumns,
  reorderArray,
  getExpandedRows,
  handleCellChange,
  handleColumnResize,
  handleLocationChanged,
  handleCanReorderRows,
  dimensionalHeaderRow,
  dimensionalFooterRow,
} from './TableHelpersOpz';

export const buildDimensionalDataTableItemRowV2 = (
  item: any,
  tableProperty_: any[],
  tableItemsLength: number,
  index: number,
  modelCurrency?: string,
  valuesLoading = false,
  withCheckboxColumn?: boolean,
) => {
  if (item?.chevronType === 'arrow-spacer') {
    const spacerHeight = Math.max(0, Number(item?.spacerHeightPx ?? 0));
    return {
      rowId: item?.id ?? `arrow-spacer-${index}`,
      height: spacerHeight,
      itemId: item?.id,
      itemName: '',
      reorderable: false,
      cells: [
        ...(withCheckboxColumn
          ? [
              {
                type: 'text' as const,
                text: '',
                nonEditable: true,
                className: 'arrow-virtual-spacer checkbox-column',
              },
            ]
          : []),
        {
          type: 'text' as const,
          text: '',
          nonEditable: true,
          className: 'arrow-virtual-spacer name first-column',
        },
        ...(tableProperty_?.map((_: any, columnIndex: number) => ({
          type: 'text' as const,
          text: '',
          nonEditable: true,
          className: `arrow-virtual-spacer ${
            columnIndex === tableProperty_.length - 1 ? ' last-index ' : ''
          }`,
        })) ?? []),
      ],
    };
  }

  const buildCells = (rowItem: any, rowIndex: number, properties: any[], currency?: string) => {
    // `checked` always starts false here — ModernDataTable overlays the real
    // checked state onto this cell after the fact (see applyCheckboxOverlay),
    // so toggling a selection doesn't have to flow back through this
    // memoized/incrementally-rebuilt row pipeline.
    const checkboxCell = withCheckboxColumn
      ? {
          type: 'checkbox' as const,
          checked: false,
          nonEditable: !!rowItem?.parentId,
          className: 'checkbox-column',
        }
      : null;

    const nameCell = {
      type: rowItem?.chevronType ? 'chevron' : 'text',
      isExpanded: false,
      hasChildren: rowItem?.chevronType,
      parentId: rowItem?.parentId,
      placeholder: 'Enter value',
      currencyType: currency,
      text: rowItem?.name ?? '',
      property: 'name',
      rows: tableItemsLength,
      hasFormulaError: rowItem?.warning?.length > 0,
      hasFormulaErrorMessage: rowItem?.warning,
      className: `name first-column ${rowItem?.parentId ? 'chevron-child' : ''} ${
        rowItem?.warning?.length > 0 ? 'formula-error ' : ''
      }`,
      isNewCell: !!rowItem?.isNewCell,
      indicatorType: rowItem?.indicator_type,
      nonEditable: !!rowItem?.parentId,
      index: rowIndex,
      ...rowItem?.cellData,
    };

    const valueCells = properties?.map((data: any, index_: number) => {
      const value = rowItem?.total_values?.find((data_: any) => data_?.name === data?.name);
      const showValueLoading = valuesLoading && !rowItem?.isNewCell;
      return {
        type: 'text',
        isNewCell: !!rowItem?.isNewCell,
        rows: tableItemsLength,
        // Raw (percentage-scaled) number as text — the cell template is the
        // single place that formats it. Passing an already-formatted string
        // here made the template's `Number(text)` parse yield NaN.
        text: showValueLoading
          ? ''
          : rowItem?.isNewCell
          ? '0'
          : value
          ? blockOutputDisplayText(value?.value, rowItem?.type)
          : '0',
        isLoading: showValueLoading,
        format: rowItem?.type,
        formatProperties: rowItem?.format_properties,
        tableName: 'block',
        currencyType: currency,
        index: rowIndex,
        data,
        nonEditable: true,
        className: `${showValueLoading ? 'block-table-cell-loading ' : ''}${
          rowItem?.parentId ? ' chevron-child ' : ''
        } ${index_ === (properties?.length ?? 0) - 1 ? ' last-index ' : ''} ${
          rowItem?.warning?.length > 0 ? 'formula-error ' : ''
        } ${data?.isActual ? 'actuals-cell' : ''}`,
        hasFormulaError: false,
      };
    });

    // Per-row delete is no longer a persistent "Action" column (matching
    // TableHelpersOpz's dimensionalHeaderRow/dimensionalFooterRow, which
    // this file re-exports) — deletion now happens through row selection +
    // BuilderModeRevamp's `SelectionActionBar`.
    return [...(checkboxCell ? [checkboxCell] : []), nameCell, ...(valueCells || [])];
  };

  return {
    rowId: item?.id ?? `${item?.name}_${item?.id ?? item?.parentId}`,
    // 34px — matches DimensionsRevamp's row height, same reasoning as
    // `TableHelpersOpz`'s header/footer rows.
    height: 34,
    itemId: item?.id ?? item?.indicatorId,
    itemName: item?.name,
    reorderable: !item?.parentId,
    isNewCell: !!item?.isNewCell,
    cells: buildCells(item, index, tableProperty_, modelCurrency),
  };
};

export const getDimensionalRowsV2 = (
  tableItemsProperty_: any[],
  tableProperty_: any[],
  modelCurrency?: string,
  valuesLoading = false,
  options?: { includeFooterRow?: boolean; withCheckboxColumn?: boolean },
) => {
  const includeFooterRow = options?.includeFooterRow ?? true;
  const withCheckboxColumn = options?.withCheckboxColumn ?? false;
  const headerRow = dimensionalHeaderRow(
    tableProperty_,
    withCheckboxColumn ? { checked: false, indeterminate: false } : undefined,
  );
  const footerRow = dimensionalFooterRow(tableProperty_, withCheckboxColumn);

  const processItems = (items: any[]) => {
    return items.map((item: any, index: number) =>
      buildDimensionalDataTableItemRowV2(
        item,
        tableProperty_,
        tableItemsProperty_?.length ?? 0,
        index,
        modelCurrency,
        valuesLoading,
        withCheckboxColumn,
      ),
    );
  };

  return [
    headerRow,
    ...(tableItemsProperty_?.length ? processItems(tableItemsProperty_) : []),
    ...(includeFooterRow ? [footerRow] : []),
  ];
};
